import { config } from '@app/config';
import { growthbook } from '@app/core/integrations/growthbook';
import FacebookIcon from '@mui/icons-material/Facebook';
import InstagramIcon from '@mui/icons-material/Instagram';
import ScienceIcon from '@mui/icons-material/Science';
import XIcon from '@mui/icons-material/X';
import YouTubeIcon from '@mui/icons-material/YouTube';
import { SvgIconProps, SxProps, Theme } from '@mui/material';
import React from 'react';
import { DailymotionIcon } from '../platforms/dailymotion/DailymotionIcon';
import { RudoIcon } from '../platforms/rudo/RudoIcon';
import { TikTokIcon } from '../platforms/tiktok/components/TiktokIcon';
import { UploadDtoPlatformEnum } from '../social-export.types';
import { resolvePlatformColor, SocialPlatformColor } from './platform-colors';
export const SocialPlatformEnum = UploadDtoPlatformEnum;

export type SocialPlatformId = (typeof SocialPlatformEnum)[keyof typeof SocialPlatformEnum];

export type SocialPlatformIconProps = SvgIconProps & {
  /** When true, applies the platform's main brand color to the icon. */
  colored?: boolean;
};

export type SocialPlatformOptions = {
  /**
   * Whether connections expose a real user/social profile (photo, display name).
   * CMS-style integrations set this to false: avatar is the platform icon and the
   * display label falls back to a generic default when no custom name is set.
   * @default true
   */
  hasUserProfile?: boolean;
};

export class SocialPlatform {
  /**
   * When false, connections have no OAuth/user profile — use platform icon + default label.
   */
  readonly hasUserProfile: boolean;

  constructor(
    readonly id: SocialPlatformId,
    readonly name: string,
    readonly _icon: React.ReactElement,
    /** Main brand color for the platform. Used when Icon({ colored: true }). */
    readonly color?: SocialPlatformColor,
    options?: SocialPlatformOptions,
  ) {
    this.hasUserProfile = options?.hasUserProfile ?? true;
  }

  Icon(props?: SocialPlatformIconProps) {
    const { colored, sx, ...rest } = props ?? {};
    const color = this.color;

    // The brand color is applied through an sx callback so it is resolved against the
    // active palette mode at render time, keeping any sx the caller passed in.
    const mergedSx: SxProps<Theme> | undefined =
      colored && color
        ? [
            ...(Array.isArray(sx) ? sx : [sx ?? {}]),
            (theme: Theme) => ({ color: resolvePlatformColor(color, theme.palette.mode) }),
          ]
        : sx;

    return React.cloneElement(this._icon, {
      ...rest,
      sx: mergedSx,
    });
  }
}

export const YoutubePlatform = new SocialPlatform(
  SocialPlatformEnum.YOUTUBE,
  'YouTube',
  <YouTubeIcon />,
  '#FF0000',
);

export const TwitterPlatform = new SocialPlatform(SocialPlatformEnum.TWITTER, 'X', <XIcon />, {
  light: '#000000',
  dark: '#E7E9EA',
});

export const InstagramPlatform = new SocialPlatform(
  SocialPlatformEnum.INSTAGRAM,
  'Instagram',
  <InstagramIcon />,
  '#E4405F',
);

export const DailymotionPlatform = new SocialPlatform(
  SocialPlatformEnum.DAILYMOTION,
  'Dailymotion',
  <DailymotionIcon />,
  { light: '#0d0d0d', dark: '#4DA3FF' },
);

export const FacebookPlatform = new SocialPlatform(
  SocialPlatformEnum.FACEBOOK,
  'Facebook',
  <FacebookIcon />,
  '#1877F2',
);

export const TikTokPlatform = new SocialPlatform(
  SocialPlatformEnum.TIKTOK,
  'TikTok',
  <TikTokIcon />,
  { light: '#000000', dark: '#25F4EE' },
);

export const RudoPlatform = new SocialPlatform(
  SocialPlatformEnum.RUDO,
  'Rudo',
  <RudoIcon />,
  '#ff4e46',
  { hasUserProfile: false },
);

export const DemoPlatform = new SocialPlatform(
  SocialPlatformEnum.DEMO,
  'Demo',
  <ScienceIcon />,
  '#01897c',
);

export class SocialPlatforms {
  private readonly basePlatforms = [
    YoutubePlatform,
    TwitterPlatform,
    InstagramPlatform,
    DailymotionPlatform,
    FacebookPlatform,
    TikTokPlatform,
    RudoPlatform,
  ];

  /** All registered platforms, sorted by display name. */
  all() {
    const isDemoEnabled =
      config.isNodeDev || growthbook.isOn('app__social_export_demo_platform_enabled');

    return [...this.basePlatforms, ...(isDemoEnabled ? [DemoPlatform] : [])].sort((a, b) =>
      a.name.localeCompare(b.name),
    );
  }

  get(id: string) {
    const platform = this.all().find((p) => p.id.toLowerCase() === id.toLowerCase());

    if (!platform) {
      throw new Error(`Platform ${id} not found`);
    }

    return platform;
  }
}

export const socialPlatforms = new SocialPlatforms();
